import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SortConfigFormItems from '../../production/form/SortConfigFormItems';
import { GROUP_BY_FIELD_NAMES } from '../components/forms/groupByConfigurations/groupByConstants';
import { StepsIndexConstant } from '../components/Stepper';
import { useStepper } from '../components/StepperContext';
import type { Entity } from '../constants/customSortFieldConfigs';
import { FormProvider } from '../form/FormContext';
import FormModalInfoAlert from '../form/FormModalInfoAlert';
import FormPanel from '../form/FormPanel';
import type { EntityReportAdapter } from './adapters/EntityReportAdapter';
import ReportLayoutConfigFormItems from './ReportLayoutConfigFormItems';
import type { GroupOption, ReportConfigurations } from './reportTypes';
import { getDefaultReportConfigurations, resolveReportConfigureValues } from './reportUtils';

export default function ReportConfigureStep({ entityReportAdapter }: { entityReportAdapter: EntityReportAdapter }) {
    const entity = entityReportAdapter.entity as Entity;
    const { getStepData, activeStep, setStepData } = useStepper();
    const reportConfigurationData = getStepData(activeStep);
    const templateSelectionData = getStepData(StepsIndexConstant.zero);
    const templateReportConfigurations = templateSelectionData?.reportConfigurations as
        | ReportConfigurations
        | undefined;
    const defaultConfig = useMemo(() => getDefaultReportConfigurations(entity), [entity]);
    const fallbackConfig = templateReportConfigurations ?? defaultConfig;
    const ConfigureFormComponent = useMemo(
        () => entityReportAdapter?.getConfigureFormComponent?.() || SortConfigFormItems,
        [entityReportAdapter]
    );

    const [values, setValues] = useState<Record<string, any>>(() =>
        resolveReportConfigureValues(reportConfigurationData, fallbackConfig, entity)
    );

    const previousWorkspaceGroupByRef = useRef<GroupOption[]>(values?.[GROUP_BY_FIELD_NAMES.workspaceGroupBy] || []);

    useEffect(() => {
        if (reportConfigurationData) {
            return;
        }
        const nextValues = resolveReportConfigureValues(undefined, fallbackConfig, entity);
        previousWorkspaceGroupByRef.current = nextValues[GROUP_BY_FIELD_NAMES.workspaceGroupBy] || [];
        setValues(nextValues);
    }, [fallbackConfig, reportConfigurationData, entity]);

    useEffect(() => {
        if (!isEqual(reportConfigurationData, values)) {
            setStepData(activeStep, values);
        }
    }, [reportConfigurationData, values, setStepData, activeStep]);

    return (
        <FormProvider state={values} setState={setValues} disableFunctionalStateUpdate>
            <FormModalInfoAlert
                descriptionToken={'report.stepper.template.configure.description'}
                alertSx={{ py: 1 }}
            />
            <FormPanel sx={{ pt: 1 }}>
                <ConfigureFormComponent titleToken="report.stepper.template.configure.form.title" entity={entity} />
                <ReportLayoutConfigFormItems />
            </FormPanel>
        </FormProvider>
    );
}
